import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
  useState
} from "react";

import { en } from "./en";
import { ru } from "./ru";
import type { LandingCopy, LandingLocale } from "./types";

const dictionaries: Record<LandingLocale, LandingCopy> = { en, ru };

interface I18nContextValue {
  readonly locale: LandingLocale;
  readonly copy: LandingCopy;
  readonly setLocale: (locale: LandingLocale) => void;
  readonly localize: (value: Record<LandingLocale, string>) => string;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

export function I18nProvider({ children }: PropsWithChildren) {
  const [locale, setLocale] = useState<LandingLocale>(() => {
    const stored = window.localStorage.getItem("nvp.locale");
    return stored === "ru" ? "ru" : "en";
  });

  const value = useMemo<I18nContextValue>(() => {
    const copy = dictionaries[locale];
    return {
      locale,
      copy,
      setLocale: (nextLocale) => {
        window.localStorage.setItem("nvp.locale", nextLocale);
        setLocale(nextLocale);
      },
      localize: (localized) => localized[locale] ?? localized.en
    };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const value = useContext(I18nContext);
  if (!value) {
    throw new Error("useI18n must be used inside I18nProvider");
  }
  return value;
}

export type { LandingLocale };
